import React, {
  useState,
  useCallback,
  useEffect,
  createContext,
  useContext,
} from 'react';
import {
  getConversations,
  putMessage,
  postCreateConversation,
  getAllJobs,
  fetcher,
} from 'utils/network/http';
import { sendEventToSegment } from 'utils/functions/Tracking';
import TRACKING_EVENTS from 'utils/constants/trackingEvents';
import Notification from 'components/Notification';
import { CHAT_ROUTES } from 'utils/constants/chat';
import { useAuth } from 'hooks/useAuth/useAuth';
import useWebsockets from 'hooks/useWebsockets/useWebsockets';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import useSWR, { cache } from 'swr';
import { NOTIFICATION_TYPES } from 'utils/constants/notifications';

const chatContext = createContext();

export function ProvideChat({ children }) {
  const chat = useProvideConversation();
  return <chatContext.Provider value={chat}>{children}</chatContext.Provider>;
}

export const useConversation = () => {
  return useContext(chatContext);
};

function useProvideConversation() {
  const [messageEvent, setMessageEvent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [chatRoute, setChatRoute] = useState(CHAT_ROUTES.CLOSED);
  const { termsAccepted } = useAuth();
  const {
    data: conversationsData,
    mutate: refreshConversations,
    error: conversationsError,
  } = useSWR(termsAccepted ? getConversations : null, fetcher, {
    refreshInterval: 60000,
  });
  const { data: journeysData, error: journeysError } = useSWR(
    termsAccepted ? getAllJobs : null,
    fetcher,
    {}
  );
  const {
    data: messagesData,
    mutate: refreshMessages,
    error: messagesError,
  } = useSWR(
    termsAccepted && conversationId
      ? `ticketing/conversations/${conversationId}/messages/`
      : null,
    fetcher,
    {}
  );
  const {
    data: conversationData,
    mutate: refreshConversation,
    error: conversationDataError,
  } = useSWR(
    termsAccepted && conversationId
      ? `ticketing/conversations/${conversationId}/`
      : null,
    fetcher,
    {}
  );

  const cleanState = useCallback(() => {
    setConversationId(null);
    setMessageEvent(null);
    setSendingMessage(false);
    setChatRoute(CHAT_ROUTES.CLOSED);
    cache.clear();
  }, []);

  const clearConversationId = () => setConversationId(null);

  const sortConversationsByDateLastMessage = (conversations) => {
    if (!conversations) return null;
    return conversations.sort((a, b) => {
      if (!a.last_message?.created_at) return 1;
      if (a.last_message?.created_at && b.last_message?.created_at) {
        return (
          moment(b.last_message?.created_at).format('x') -
          moment(a.last_message?.created_at).format('x')
        );
      }
      return -1;
    });
  };

  const handleGoToRoute = useCallback((route) => {
    setChatRoute(route);
    sendEventToSegment(TRACKING_EVENTS.CHAT_PAGEVIEW, {
      pathname: route,
    });
  }, []);

  const updateUnreadMessagesFlag = (conversations, id) => {
    if (!conversations) return [];
    return conversations.map((conversation) =>
      conversation.id === id
        ? { ...conversation, has_unread_messages: false }
        : conversation
    );
  };

  const handleFetchAndGoToConversation = useCallback(
    (id, from) => {
      setConversationId(id);
      refreshConversation();
      refreshMessages();
      refreshConversations({
        data: {
          results: updateUnreadMessagesFlag(
            conversationsData?.data?.results,
            id
          ),
        },
      });
      setChatRoute(CHAT_ROUTES.CONVERSATION);
      sendEventToSegment(TRACKING_EVENTS.CHAT_CONVERSATION_OPEN, {
        conversationId: id,
        from,
      });
    },
    [
      refreshConversations,
      refreshConversation,
      refreshMessages,
      conversationsData,
    ]
  );

  const handleCreateConversation = useCallback(
    async (subject, journeyId, from) => {
      try {
        setLoading(true);
        const { data } = await postCreateConversation(subject, journeyId);
        handleFetchAndGoToConversation(data?.id, from);
      } catch (e) {
        setError(true);
        console.error(e);
      } finally {
        setLoading(false);
        sendEventToSegment(TRACKING_EVENTS.CHAT_CONVERSATION_CREATE, {
          subject,
          from,
        });
      }
    },
    [handleFetchAndGoToConversation]
  );

  const updateNewMessage = useCallback(
    (message, isAgent) => {
      if (!messagesData?.data?.results) return [];
      return [
        ...messagesData.data.results,
        {
          created_at: moment().format(),
          message,
          read: true,
          sender: { agent: isAgent },
        },
      ];
    },
    [messagesData]
  );

  const handleSendMessage = useCallback(
    async (message) => {
      if (!message) return;
      refreshMessages(
        {
          data: {
            results: updateNewMessage(message, false),
          },
        },
        false
      );
      try {
        setSendingMessage(true);
        const { data } = await putMessage(conversationId, message);
        refreshMessages(
          {
            data: {
              results: data.results,
            },
          },
          false
        );
        sendEventToSegment(TRACKING_EVENTS.CHAT_CONVERSATION_SEND, {
          conversationId,
        });
      } catch (e) {
        setError(true);
        console.error(e);
      } finally {
        setSendingMessage(false);
      }
    },
    [conversationId, refreshMessages, updateNewMessage]
  );

  const getConversationName = useCallback(
    (convId) => {
      if (!conversationsData?.data?.results) return '';
      return conversationsData?.data?.results.find(
        (conversation) => conversation.id === convId
      )?.subject;
    },
    [conversationsData]
  );

  const handleMessageEvent = useCallback(() => {
    if (!messageEvent || !messageEvent.conversation_id || !messageEvent.message)
      return;
    if (messageEvent.conversation_id !== conversationId) {
      const subject = getConversationName(messageEvent.conversation_id);
      toast(
        <Notification
          title={subject}
          type={NOTIFICATION_TYPES.INFO}
          content={messageEvent.message}
          handleClick={() =>
            handleFetchAndGoToConversation(messageEvent.conversation_id)
          }
        />
      );
    } else {
      const { type } = messageEvent;
      if (!['agent_message', 'driver_message'].includes(type)) return;
      refreshMessages();
    }
    refreshConversations();
    setMessageEvent(null);
  }, [
    handleFetchAndGoToConversation,
    conversationId,
    messageEvent,
    getConversationName,
    refreshMessages,
    refreshConversations,
  ]);

  useEffect(() => {
    if (messageEvent) {
      handleMessageEvent();
    }
  }, [messageEvent, handleMessageEvent]);

  const handleRealTimeMessages = useCallback((event) => {
    setMessageEvent(event);
  }, []);

  useWebsockets({
    channel: 'cs_chat',
    event: 'user-notifications',
    eventHandler: handleRealTimeMessages,
  });

  const filterAlreadyWithConversation = useCallback(
    (journeys) => {
      if (!journeys || !conversationsData) return null;
      const conversations = conversationsData?.data?.results;
      return journeys.filter(
        (journey) =>
          conversations.findIndex(
            (conversation) => conversation.journey?.id === journey.id
          ) === -1
      );
    },
    [conversationsData]
  );

  const checksIfHasConversationAndReturnId = useCallback(
    (journeyId) => {
      if (!conversationsData) return undefined;
      const conversations = conversationsData?.data?.results;
      return conversations.find(
        (conversation) => conversation.journey?.id === journeyId
      )?.id;
    },
    [conversationsData]
  );

  const checkIfThereAreUnreadMessages = (conversations) => {
    if (!conversations) return false;
    return conversations.some(
      (conversation) => conversation.has_unread_messages
    );
  };

  return {
    conversations: sortConversationsByDateLastMessage(
      conversationsData?.data?.results
    ),
    messages: messagesData?.data?.results || [],
    conversation: conversationData?.data || null,
    loading,
    sendingMessage,
    handleSendMessage,
    handleCreateConversation,
    chatRoute,
    handleGoToRoute,
    conversationId,
    handleRealTimeMessages,
    journeys:
      filterAlreadyWithConversation(journeysData?.data?.results) || null,
    error,
    hasUnreadMessages: checkIfThereAreUnreadMessages(
      conversationsData?.data?.results
    ),
    clearConversationId,
    handleFetchAndGoToConversation,
    cleanState,
    checksIfHasConversationAndReturnId,
    conversationsError,
    journeysError,
    messagesError,
    conversationDataError,
  };
}
